/**
 * Breezi SDK Script-Tag Widget
 * Zero-friction PayPal integration for no-code users
 * Usage: <div data-breezi-paypal data-client-id="..." data-amount="29.99">Pay Now</div>
 */

interface BreeziWidgetConfig {
  clientId: string;
  amount: string;
  currency: string;
  environment: 'sandbox' | 'production';
  onSuccess?: string;
  onError?: string;
  onCancel?: string;
}

interface PayPalPayment {
  id: string;
  status: string;
  purchase_units: Array<{
    amount: {
      value: string;
      currency_code: string;
    };
  }>;
  payer: {
    email_address: string;
    payer_id: string;
  };
}

class BreeziPayPalWidget {
  private config: BreeziWidgetConfig;
  private element: HTMLElement;

  constructor(element: HTMLElement) {
    this.element = element;
    this.config = this.parseConfig(element);
    this.init();
  }

  private parseConfig(element: HTMLElement): BreeziWidgetConfig {
    const dataset = element.dataset;
    
    if (!dataset.breeziPaypal) {
      throw new Error('Missing data-breezi-paypal attribute');
    }
    
    if (!dataset.clientId) {
      throw new Error('Missing data-client-id attribute');
    }
    
    if (!dataset.amount) {
      throw new Error('Missing data-amount attribute');
    }

    return {
      clientId: dataset.clientId,
      amount: dataset.amount,
      currency: dataset.currency || 'USD',
      environment: (dataset.environment as 'sandbox' | 'production') || 'sandbox',
      onSuccess: dataset.onSuccess,
      onError: dataset.onError,
      onCancel: dataset.onCancel
    };
  }

  private async init() {
    try {
      this.showLoading();
      await this.loadPayPalSDK();
      await this.initPayPalButton();
    } catch (error) {
      this.showError(error as Error);
    }
  }

  private async loadPayPalSDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).paypal) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${this.config.clientId}&currency=${this.config.currency}&components=buttons`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
      document.head.appendChild(script);
    });
  }

  private async initPayPalButton() {
    // Create PayPal button container
    const buttonContainer = document.createElement('div');
    buttonContainer.id = `breezi-paypal-${Date.now()}`;
    this.element.innerHTML = '';
    this.element.appendChild(buttonContainer);
    
    // Initialize PayPal buttons
    (window as any).paypal.Buttons({
      createOrder: (data: any, actions: any) => {
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: this.config.amount,
              currency_code: this.config.currency
            }
          }]
        });
      },
      onApprove: async (data: any, actions: any) => {
        try {
          const order = await actions.order.capture();
          this.handleSuccess(order);
        } catch (error) {
          this.handleError(error as Error);
        }
      },
      onError: (err: any) => {
        this.handleError(new Error(`PayPal Error: ${err}`));
      },
      onCancel: () => {
        this.handleCancel();
      },
      style: {
        color: 'blue',
        shape: 'rect',
        label: 'paypal',
        height: 40,
      }
    }).render(buttonContainer);
  }

  private showLoading() {
    this.element.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; padding: 12px 24px; background: #f3f4f6; border-radius: 6px; color: #6b7280;">
        <div style="width: 16px; height: 16px; border: 2px solid #d1d5db; border-top: 2px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-right: 8px;"></div>
        Loading PayPal...
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
  }

  private showError(error: Error) {
    this.element.innerHTML = `
      <div style="padding: 12px 24px; background: #fee2e2; border: 1px solid #fecaca; border-radius: 6px; color: #dc2626;">
        <strong>Payment Error:</strong> ${error.message}
      </div>
    `;
    
    this.handleError(error);
  }

  private handleSuccess(payment: PayPalPayment) {
    console.log('✅ Payment successful:', payment);
    
    // Show success message
    this.element.innerHTML = `
      <div style="padding: 12px 24px; background: #d1fae5; border: 1px solid #a7f3d0; border-radius: 6px; color: #065f46;">
        <strong>✅ Payment Successful!</strong><br>
        Payment ID: ${payment.id}<br>
        Amount: ${payment.purchase_units[0].amount.value} ${payment.purchase_units[0].amount.currency_code}
      </div>
    `;
    
    // Call custom success handler if provided
    if (this.config.onSuccess && typeof (window as any)[this.config.onSuccess] === 'function') {
      (window as any)[this.config.onSuccess](payment);
    }
    
    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('breezi:payment:success', {
      detail: payment,
      bubbles: true
    }));
  }

  private handleError(error: Error) {
    console.error('❌ Payment error:', error);
    
    // Call custom error handler if provided
    if (this.config.onError && typeof (window as any)[this.config.onError] === 'function') {
      (window as any)[this.config.onError](error);
    }
    
    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('breezi:payment:error', {
      detail: error,
      bubbles: true
    }));
  }

  private handleCancel() {
    console.log('❌ Payment cancelled by user');
    
    // Show cancelled message
    this.element.innerHTML = `
      <div style="padding: 12px 24px; background: #fef3c7; border: 1px solid #fde68a; border-radius: 6px; color: #92400e;">
        <strong>Payment Cancelled</strong><br>
        The payment was cancelled by the user.
      </div>
    `;
    
    // Call custom cancel handler if provided
    if (this.config.onCancel && typeof (window as any)[this.config.onCancel] === 'function') {
      (window as any)[this.config.onCancel]();
    }
    
    // Dispatch custom event
    this.element.dispatchEvent(new CustomEvent('breezi:payment:cancel', {
      bubbles: true
    }));
  }
}

// Auto-initialize widgets when DOM is ready
function initializeBreeziWidgets() {
  const widgets = document.querySelectorAll('[data-breezi-paypal]');
  widgets.forEach((element) => {
    try {
      new BreeziPayPalWidget(element as HTMLElement);
    } catch (error) {
      console.error('Failed to initialize Breezi widget:', error);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeBreeziWidgets);
} else {
  initializeBreeziWidgets();
}

// Re-initialize when new elements are added (for dynamic content)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        if (element.hasAttribute('data-breezi-paypal')) {
          try {
            new BreeziPayPalWidget(element as HTMLElement);
          } catch (error) {
            console.error('Failed to initialize Breezi widget:', error);
          }
        }
        
        // Check child elements
        const childWidgets = element.querySelectorAll('[data-breezi-paypal]');
        childWidgets.forEach((childElement) => {
          try {
            new BreeziPayPalWidget(childElement as HTMLElement);
          } catch (error) {
            console.error('Failed to initialize Breezi widget:', error);
          }
        });
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for manual initialization
(window as any).BreeziPayPalWidget = BreeziPayPalWidget;
(window as any).initializeBreeziWidgets = initializeBreeziWidgets;